import React from "react";
import * as styles from "./PanelComponent.module.scss";
import { Divider } from "cprime-foundational-components";
import { useDispatch, useSelector } from "react-redux";
import {
  createFeature,
  editFeature,
  deleteFeature,
} from "../../store/slices/createFeatureSlice";
import { AppDispatch, RootState } from "../../store/store";
import { triggerToast } from "../../utils/toast";
import { incrementTableRefresh } from "../../store/slices/apiDetailsSlice";
import Button from "../Common/CprimeButton";
const scssClass = styles as any;

interface FooterButtonsProps {
  isEdit: boolean;
  onCancel: () => void;
  getApplicationDetails: any;
}

const FooterButtons: React.FC<FooterButtonsProps> = ({
  isEdit,
  onCancel,
  getApplicationDetails,
}) => {
  const saveFeatureData = useSelector(
    (state: RootState) => state?.featureList?.saveData,
  );
  const editFeatureData = useSelector(
    (state: RootState) => state?.featureList?.editData,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);

  const handleSave = async () => {
    setLoading(true);
    const body = {
      featureName: saveFeatureData?.feature_name,
      featureDescription: saveFeatureData?.feature_description,
      groupId: saveFeatureData?.group_id,
      appId: saveFeatureData?.appId,
      permissionId: saveFeatureData?.permissionId,
      isOpenToAll: saveFeatureData?.visibleToRoles,
      ...(saveFeatureData?.visibleToRoles === false && {
        roleIds: saveFeatureData?.allowedRoles,
      }),
      ...(isEdit && { id: saveFeatureData?.id }),
    };

    const action = isEdit ? editFeature : createFeature;

    try {
      const result = await dispatch(action(body))?.unwrap();
      if (result?.status === "SUCCESS") {
        triggerToast(result?.message, "success");
        onCancel();
        dispatch(incrementTableRefresh());
        getApplicationDetails();
      } else {
        triggerToast(
          result?.message || "Error encountered while saving feature.",
          "error",
        );
      }
    } catch (error: any) {
      let errorMessage =
        error?.message ||
        "An unexpected error occurred while saving the feature.";
      triggerToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const checkDisabled = () => {
    // Edit mode, permissions empty
    if (isEdit && saveFeatureData?.permissionId?.length === 0) {
      return true;
    }

    // Create mode validation
    if (
      !isEdit &&
      saveFeatureData &&
      saveFeatureData?.feature_name !== "" &&
      saveFeatureData?.feature_description !== "" &&
      saveFeatureData?.permissionId?.length !== 0 &&
      (saveFeatureData?.visibleToRoles !== false ||
        (saveFeatureData?.visibleToRoles === false &&
          saveFeatureData?.allowedRoles?.length > 0))
    ) {
      return false;
    }

    // EDIT MODE CHECKS
    if (isEdit && editFeatureData && saveFeatureData) {
      // Check for empty values in saveFeatureData
      const hasEmptyValues =
        saveFeatureData?.feature_name === "" ||
        saveFeatureData?.feature_description === "" ||
        saveFeatureData?.permissionId?.length === 0 ||
        (saveFeatureData?.visibleToRoles === false &&
          (!saveFeatureData?.allowedRoles ||
            saveFeatureData?.allowedRoles?.length === 0));

      if (hasEmptyValues) return true; // disable

      // Check if data is unchanged
      const isSame =
        editFeatureData?.feature_name === saveFeatureData?.feature_name &&
        editFeatureData?.feature_description ===
          saveFeatureData?.feature_description &&
        Boolean(editFeatureData?.is_open_to_all) ===
          Boolean(saveFeatureData?.visibleToRoles) &&
        JSON.stringify(saveFeatureData?.group_name ?? []) ===
          JSON.stringify(
            editFeatureData?.group_name === ""
              ? []
              : (editFeatureData?.group_name?.split(",") ?? []),
          ) &&
        JSON.stringify(
          (editFeatureData.permission_ids ?? [])?.map(Number)?.sort(),
        ) ===
          JSON.stringify(
            (saveFeatureData.permissionId ?? [])?.map(Number)?.sort(),
          ) &&
        JSON.stringify(
          (editFeatureData.role_ids ?? [])?.map(Number)?.sort(),
        ) ===
          JSON.stringify(
            (saveFeatureData.allowedRoles ?? [])?.map(Number)?.sort(),
          );

      // If nothing changed = disable
      if (isSame) return true;

      // Valid + changed = enable
      return false;
    }
    return true;
  };

  const onDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await dispatch(
        deleteFeature(saveFeatureData?.id),
      ).unwrap();
      if (result?.status === "SUCCESS") {
        triggerToast("Feature deleted successfully", "success");
        onCancel();
        dispatch(incrementTableRefresh());
        getApplicationDetails();
      } else {
        triggerToast(result?.message, "error");
      }
    } catch (error) {
      triggerToast("Error deleting feature", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className={scssClass.bottomPanel}>
      <Divider
        variant={"fullWidth"}
        sx={{
          marginBottom: "15px",
          width: "100%",
          marginLeft: "0px !important",
          backgroundColor: "#f8f8f9 !important",
        }}
      />
      <div className={scssClass.footerButtons}>
        <div className={scssClass.leftButtons}>
          {isEdit && (
            <Button variant="danger" onClick={onDelete} loading={deleteLoading}>
              Delete Feature
            </Button>
          )}
        </div>
        <div className={scssClass.rightButtons}>
          <Button
            variant="tertiary"
            sx={{ marginRight: "10px" }}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            sx={{ marginLeft: "5px", marginRight: "10px" }}
            onClick={handleSave}
            disabled={checkDisabled()}
            loading={loading}
          >
            Save Feature
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FooterButtons;
